/**
 * This is the FIND YOUR COMMUNITY PAGE (a variant of the NATIONAL STANDARD PAGE)
 *
 * This generates all the filterable paths for the FIND YOUR COMMUNITY page
 *
 * This is a catch-all route that will match any path in the format /find-your-community/state/region/area/suburb
 * Paths not generated (invalid paths) will return a 404
 */
import React from 'react';
import { GetStaticProps } from 'next';
import { apolloClient, setApolloHeaders } from '@src/services/apollo';
import {
  pageNationalStandardQuery,
  pageNationalStandardPart1ContentBlocksQuery,
  pageNationalStandardPart2ContentBlocksQuery,
  pageNationalStandardPart3ContentBlocksQuery,
  pageNationalStandardPart4ContentBlocksQuery
} from '@src/graphql/queries/pageNationalStandard.query';
import PageNationalStandardTemplate from '@src/templates/PageNationalStandard';
import {
  CommunityMapPathsQuery,
  GlobalSettingsQuery,
  MapCommunityDetailQuery,
  NationalBasePageQuery,
  PageNationalStandardPart1ContentBlocksQuery,
  PageNationalStandardPart2ContentBlocksQuery,
  PageNationalStandardPart3ContentBlocksQuery,
  PageNationalStandardPart4ContentBlocksQuery,
  PageNationalStandardQuery,
  PageNationalStandardQueryVariables
} from '@src/graphql/gql-types';
import {
  globalSettingsQuery,
  mapCommunityDetail,
  nationalBasePageQuery
} from '@src/graphql/queries';
import { NationalStandardPageProps } from '@src/types/page';
import { mergeContentBlocks } from '@src/utils/helpers';
import { base64EncodeHero } from '@src/utils/build/base64EncodeHero';
import { communityMapPathsQuery } from '@src/graphql/queries/communityMapPaths.query';
import { PATHS } from '@src/constants/site';
import transformMapData from '@src/utils/build/mapData';
import { MapLocation } from '@src/types/map';
import { getFilteredMapData, getLocationName } from '@src/utils/map';
import getLocationInfoBlock from '@src/utils/build/locationInfoBlock';
import { ParsedUrlQuery } from 'querystring';

const FindYourCommunityStandardPage: React.FC<NationalStandardPageProps> = (
  props
) => {
  return <PageNationalStandardTemplate {...props} />;
};

export default FindYourCommunityStandardPage;

/**
 * Build static paths
 * - Queries all communities and builds all possible paths
 * - Only returns paths that have an entry with the filterable fields
 * - This uses nextJS [catch-all-routes](https://nextjs.org/docs/routing/dynamic-routes#catch-all-routes)
 * @returns path params
 */
export const getStaticPaths = async () => {
  const pageQueryResponse = await apolloClient.query<CommunityMapPathsQuery>({
    query: communityMapPathsQuery
  });

  if (pageQueryResponse?.errors) {
    console.error(pageQueryResponse.errors);
  }

  const pages = pageQueryResponse.data?.pagePath?.items;
  const stateSlugs = new Set<string[]>();
  const regionSlugs = new Set<string[]>();
  const areaSlugs = new Set<string[]>();
  const suburbSlugs = new Set<string[]>();

  pages?.forEach((page) => {
    const suburb = page?.communitySuburb;
    const area = suburb?.linkedFrom?.areaCollection?.items[0];
    const region = area?.linkedFrom?.regionCollection?.items[0];
    const state = region?.linkedFrom?.stateCollection?.items[0];

    if (state?.slug) {
      stateSlugs.add([state.slug]);
    }
    if (region?.slug && state?.slug) {
      regionSlugs.add([state.slug, region.slug]);
    }
    if (area?.slug && state?.slug && region?.slug) {
      areaSlugs.add([state.slug, region.slug, area.slug]);
    }
    if (suburb?.slug && state?.slug && region?.slug && area?.slug) {
      suburbSlugs.add([state.slug, region.slug, area.slug, suburb.slug]);
    }
  });

  // Generate paths for each level
  const paths = [
    {
      params: { location: [] }
    },
    ...Array.from(stateSlugs).map((stateSlug) => ({
      params: { location: stateSlug }
    })),
    ...Array.from(regionSlugs).map((regionSlug) => ({
      params: { location: regionSlug }
    })),
    ...Array.from(areaSlugs).map((areaSlug) => ({
      params: { location: areaSlug }
    })),
    ...Array.from(suburbSlugs).map((suburbSlug) => ({
      params: { location: suburbSlug }
    }))
  ];

  return {
    paths,
    fallback: false
  };
};

export const getStaticProps: GetStaticProps = async ({ params, preview }) => {
  /**
   * Always get the last item in the array, this will be the current page to display */
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/at
  const pageSlug = PATHS.FIND_YOUR_COMMUNITY;

  if (preview) {
    setApolloHeaders(process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW_TOKEN);
  }

  const [
    globalSettingsQueryResponse,
    nationalBasePageQueryResponse,
    pageQueryResponse,
    pageNationalStandardPart1ContentBlocksQueryResponse,
    pageNationalStandardPart2ContentBlocksQueryResponse,
    pageNationalStandardPart3ContentBlocksQueryResponse,
    pageNationalStandardPart4ContentBlocksQueryResponse,
    mapCommunityDetailQueryResponse
  ] = await Promise.all([
    apolloClient.query<GlobalSettingsQuery>({
      query: globalSettingsQuery,
      variables: {
        preview: preview
      }
    }),
    apolloClient.query<NationalBasePageQuery>({
      query: nationalBasePageQuery,
      variables: {
        preview: preview
      }
    }),
    apolloClient.query<
      PageNationalStandardQuery,
      PageNationalStandardQueryVariables
    >({
      query: pageNationalStandardQuery,
      variables: { slug: pageSlug, preview: preview }
    }),
    apolloClient.query<
      PageNationalStandardPart1ContentBlocksQuery,
      PageNationalStandardQueryVariables
    >({
      query: pageNationalStandardPart1ContentBlocksQuery,
      variables: { slug: pageSlug, preview: preview }
    }),
    apolloClient.query<
      PageNationalStandardPart2ContentBlocksQuery,
      PageNationalStandardQueryVariables
    >({
      query: pageNationalStandardPart2ContentBlocksQuery,
      variables: { slug: pageSlug, preview: preview }
    }),
    apolloClient.query<
      PageNationalStandardPart3ContentBlocksQuery,
      PageNationalStandardQueryVariables
    >({
      query: pageNationalStandardPart3ContentBlocksQuery,
      variables: { slug: pageSlug, preview: preview }
    }),
    apolloClient.query<
      PageNationalStandardPart4ContentBlocksQuery,
      PageNationalStandardQueryVariables
    >({
      query: pageNationalStandardPart4ContentBlocksQuery,
      variables: { slug: pageSlug, preview: preview }
    }),
    apolloClient.query<MapCommunityDetailQuery>({
      query: mapCommunityDetail,
      variables: {
        preview: preview
      }
    })
  ]);

  if (globalSettingsQueryResponse?.errors) {
    console.error(globalSettingsQueryResponse.errors);
  }

  if (nationalBasePageQueryResponse?.errors) {
    console.error(nationalBasePageQueryResponse.errors);
  }

  if (pageQueryResponse?.errors) {
    console.error(pageQueryResponse.errors);
  }

  if (pageNationalStandardPart1ContentBlocksQueryResponse?.errors) {
    console.error(pageNationalStandardPart1ContentBlocksQueryResponse.errors);
  }

  if (pageNationalStandardPart2ContentBlocksQueryResponse?.errors) {
    console.error(pageNationalStandardPart2ContentBlocksQueryResponse.errors);
  }

  if (pageNationalStandardPart3ContentBlocksQueryResponse?.errors) {
    console.error(pageNationalStandardPart3ContentBlocksQueryResponse.errors);
  }

  if (pageNationalStandardPart4ContentBlocksQueryResponse?.errors) {
    console.error(pageNationalStandardPart4ContentBlocksQueryResponse.errors);
  }

  const items = mergeContentBlocks([
    pageNationalStandardPart1ContentBlocksQueryResponse?.data,
    pageNationalStandardPart2ContentBlocksQueryResponse?.data,
    pageNationalStandardPart3ContentBlocksQueryResponse?.data,
    pageNationalStandardPart4ContentBlocksQueryResponse?.data
  ]);

  const pagePayload = await base64EncodeHero(pageQueryResponse?.data);

  const mapData = transformMapData(mapCommunityDetailQueryResponse?.data);
  const locationInfoBlock = getLocationInfoBlock(
    params as ParsedUrlQuery,
    mapCommunityDetailQueryResponse?.data
  );

  const location: MapLocation = {
    state: (params?.location?.[0] as string) || 'all',
    region: (params?.location?.[1] as string) || 'all',
    area: (params?.location?.[2] as string) || 'all',
    suburb: (params?.location?.[3] as string) || 'all'
  };

  const filteredData = getFilteredMapData(mapData.transformedData, location);
  const locationName = getLocationName(location, mapData.communityLocations);

  const templatePropsData: NationalStandardPageProps = {
    globalSettingsPayload: globalSettingsQueryResponse?.data,
    nationalBasePagePayload: nationalBasePageQueryResponse?.data,
    pagePayload: pagePayload as PageNationalStandardQuery,
    pageContentBlocks: items,
    mapData: {
      ...mapData,
      location,
      filteredData,
      locationName,
      listingsData: filteredData.slice(0, 6),
      locationInfoBlock: locationInfoBlock || null
    }
  };

  return {
    props: {
      ...templatePropsData
    }
  };
};
